import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import * as moment from 'moment';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { ACCOUNT_TYPE } from 'src/app/core/domain';
import { IBrandDetails } from 'src/app/core/domain/brand.model';
import { IProfileDetails } from 'src/app/core/domain/profile.model';
import { IProgram, ProgramType } from 'src/app/core/domain/program.model';
import { PublicApiService } from 'src/app/core/service/public-api.service';
import { getBrandDetails } from 'src/app/core/state/global';
import { getProfileData } from 'src/app/core/state/profile';
import { createSlug, sortProgramsByDisplayOrder } from 'src/app/shared/utils/common-methods';

@Component({
  selector: 'app-active-programs',
  templateUrl: './active-programs.component.html',
  styleUrls: ['./active-programs.component.scss']
})
export class ActiveProgramsComponent implements OnInit, OnDestroy {
  loading = true;
  errorMessage: string | null = null;
  activePrograms: IProgram[] = [];

  private destroyed$ = new Subject<void>();
  private refreshScheduled = false;

  brandDetails: Partial<IBrandDetails> | null = null;
  profileDetails: IProfileDetails | null = null;

  constructor(
    private store: Store,
    private publicApi: PublicApiService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.store
      .pipe(select(getBrandDetails), takeUntil(this.destroyed$))
      .subscribe((res) => {
        this.brandDetails = res || null;
        this.scheduleRefresh();
      });

    this.store
      .pipe(select(getProfileData), takeUntil(this.destroyed$))
      .subscribe((res) => {
        this.profileDetails = res || null;
        this.scheduleRefresh();
      });
  }

  private scheduleRefresh() {
    if (this.refreshScheduled) return;
    this.refreshScheduled = true;
    setTimeout(() => {
      this.refreshScheduled = false;
      this.fetchActivePrograms();
    }, 0);
  }

  private fetchActivePrograms() {
    if (!this.brandDetails?.features) {
      return;
    }

    this.loading = true;
    this.errorMessage = null;

    // Not `!this.profileDetails?.email` -- profileDetails loads async from the
    // NgRx store, and this widget is one of the first things to fetch on the
    // dashboard, so it can (and did) run before that resolves, wrongly
    // treating a logged-in spoke user as anonymous and hitting the public
    // (intentionally Hub-only, un-scoped) endpoint instead of the
    // authenticated one. localStorage's 'user' key is synchronous and
    // already the established login check elsewhere in this codebase (e.g.
    // call-for-applications-card.component.ts's handleLoginForNonLoggedInUser()).
    const isPublic = !localStorage.getItem('user');
    const partnerId = this.profileDetails?.partnerId;

    forkJoin({
      programs: this.publicApi.fetchProgramManagement('true'),
      vsPrograms: this.publicApi.fetchVsProgramManagement('true'),
      applicationPrograms: this.publicApi.fetchApplicationProgramManagement('true', isPublic, partnerId)
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ({ programs, vsPrograms, applicationPrograms }) => {
          const normalized: IProgram[] = [
            ...(programs || []).map((p) => ({ ...p, programType: ProgramType.PROGRAM })),
            ...(vsPrograms || []).map((p) => ({ ...p, programType: ProgramType.VS_PROGRAM })),
            ...(applicationPrograms || []).map((p) => ({ ...p, programType: ProgramType.CALL_FOR_APPLICATION }))
          ];

          const visible = normalized.filter((p) => !this.isUnauthorizedToApply(p));
          const sorted = sortProgramsByDisplayOrder(visible, 'applicationClosedDate');
          this.activePrograms = this.filterActivePrograms(sorted);
          this.loading = false;
        },
        error: () => {
          this.activePrograms = [];
          this.loading = false;
          this.errorMessage = 'Error while fetching active programs';
        }
      });
  }

  private today = moment().subtract(1, 'day');
  private filterActivePrograms(programs: IProgram[]) {
    return (programs || []).filter((p) => {
      if (p.applicationsClosed) return false;
      if (!p.applicationClosedDate) return true;
      return moment(p.applicationClosedDate).isSameOrAfter(this.today);
    });
  }

  trackByProgramUuid(index: number, program: IProgram) {
    return program?.uuid || index;
  }

  /* SAN-539 follow-up: for a cross-tenant clone (programRequestType ==
     'external'), promotedFromName is the raw source tenant name -- show
     just "External" instead of exposing that. Partner-shared / hub-owned
     cases still show the real partner/hub name. */
  getProvenanceBadgeLabel(program: IProgram): string {
    return program?.programRequestType === 'external' ? 'External' : program?.promotedFromName;
  }

  getProgramTypeLabel(programType: ProgramType) {
    if (programType === ProgramType.VS_PROGRAM) return 'VS Program';
    if (programType === ProgramType.PROGRAM) return 'Program';
    if (programType === ProgramType.CALL_FOR_APPLICATION) return 'Call for Applications';
    return 'Program';
  }

  openProgram(program: IProgram) {
    if (!program) return;
    const programTitleSlug = createSlug(program.programTitle || '');

    let url = `/programs/apply/${program.programCode}/${programTitleSlug}?autoStatusCheck=true`;
    if (program.programType === ProgramType.VS_PROGRAM) {
      url = `/vs-programs/details/${program.programCode}/${programTitleSlug}`;
    }
    if (program.programType === ProgramType.PROGRAM) {
      url = `/programs/details/${program.programCode}/${programTitleSlug}`;
    }

    this.router.navigateByUrl(url);
  }

  private isUnauthorizedToApply(program: IProgram) {
    if (!this.profileDetails) return false;

    if (this.profileDetails.accountType === ACCOUNT_TYPE.STARTUP) {
      return program.programType === ProgramType.VS_PROGRAM;
    }

    if (this.profileDetails.accountType === ACCOUNT_TYPE.INDIVIDUAL) {
      return program.programType === ProgramType.PROGRAM;
    }

    if (
      this.profileDetails.accountType === ACCOUNT_TYPE.INVESTOR ||
      this.profileDetails.accountType === ACCOUNT_TYPE.CORPORATE ||
      this.profileDetails.accountType === ACCOUNT_TYPE.MENTOR ||
      this.profileDetails.accountType === ACCOUNT_TYPE.SERVICE_PROVIDER ||
      this.profileDetails.accountType === ACCOUNT_TYPE.PARTNER ||
      this.profileDetails.accountType === ACCOUNT_TYPE.PROGRAM_OFFICE
    ) {
      return program.programType === ProgramType.VS_PROGRAM || program.programType === ProgramType.PROGRAM;
    }

    return false;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
